<script lang="ts" setup>
import { ref, onMounted } from "vue";
import { useRouter } from "vue-router";
import PanelGrid from "@/views/shared/components/PanelGrid.vue";
import PanelCard from "@/views/shared/components/PanelCard.vue";
import PageHeader from "@/components/PageHeader.vue";

const router = useRouter();
const isLoading = ref(false);
const isSaving = ref(false);
const showDeleteModal = ref(false);

const partnerSettings = ref({
  organizationName: "Partner Organization",
  contactEmail: "admin@partner.com",
  apiUrl: "https://api.partner.com",
  maxClients: 50,
  enableNotifications: true,
  enableWebhooks: false,
  webhookUrl: "",
  billingContact: "billing@partner.com",
  supportContact: "support@partner.com",
});

const notificationSettings = ref({
  emailOnNewClient: true,
  emailOnClientError: true,
  emailOnSystemUpdates: false,
  weeklyReports: true,
  monthlyReports: true,
});

const apiSettings = ref({
  apiKeyVisible: false,
  apiKey: "pk_live_1234567890abcdef",
  enableRateLimit: true,
  rateLimit: 1000,
  enableLogging: true,
  logRetentionDays: 30,
});

const billingSettings = ref({
  billingModel: "per-client",
  monthlyFee: 29.99,
  currency: "USD",
  autoInvoicing: true,
  paymentMethod: "credit-card",
});

onMounted(async () => {
  await loadPartnerSettings();
});

async function loadPartnerSettings() {
  isLoading.value = true;
  try {
    // TODO: Load actual settings from API
  } finally {
    isLoading.value = false;
  }
}

async function saveSettings() {
  isSaving.value = true;
  try {
    // TODO: Save settings to API
    await new Promise((resolve) => setTimeout(resolve, 1000));
  } finally {
    isSaving.value = false;
  }
}

function toggleApiKeyVisibility() {
  apiSettings.value.apiKeyVisible = !apiSettings.value.apiKeyVisible;
}

function regenerateApiKey() {
  apiSettings.value.apiKey = "pk_live_" + Math.random().toString(36).substring(2, 18);
}

function confirmDeleteAccount() {
  showDeleteModal.value = false;
  // TODO: Delete partner account via API
}
</script>

<template>
  <PanelGrid :columns="1">
    <PanelCard>
      <PageHeader
        title="Partner Settings"
        :breadcrumbs="[
          { label: 'Dashboard', href: '/' },
          { label: 'Partners', href: '/partners' },
          { label: 'Settings' },
        ]"
      >
        <template #actions>
          <router-link to="/partners" class="bb-btn-secondary-cta d-none d-md-inline">
            <i class="bi bi-arrow-left me-2"></i>Back to Overview
          </router-link>
        </template>
      </PageHeader>

      <!-- Coming Soon Banner — the settings below are demo data with no real backing yet. The page
           is left visible so the layout/intent is clear; everything is greyed out + pointer-disabled
           so partners don't act on the controls. Mirrors the publisher analytics treatment. -->
      <div class="alert alert-info d-flex align-items-center mb-4" role="alert">
        <i class="bi bi-info-circle me-2"></i>
        <span>Partner settings are coming soon. The options shown below are illustrative only.</span>
      </div>

      <div v-if="isLoading" class="text-center py-5">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading settings...</span>
        </div>
      </div>

      <form v-else @submit.prevent="saveSettings" class="mt-4 coming-soon">
        <!-- Account Information -->
        <div class="card mb-4">
          <div class="card-body">
            <h2 class="card-title">Account Information</h2>

            <div class="row mb-3">
              <label for="orgName" class="col-sm-3 col-form-label">Organisation Name</label>
              <div class="col-sm-9">
                <input
                  id="orgName"
                  type="text"
                  class="form-control"
                  v-model="partnerSettings.organizationName"
                />
              </div>
            </div>

            <div class="row mb-3">
              <label for="contactEmail" class="col-sm-3 col-form-label">Contact Email</label>
              <div class="col-sm-9">
                <input
                  id="contactEmail"
                  type="email"
                  class="form-control"
                  v-model="partnerSettings.contactEmail"
                />
              </div>
            </div>

            <div class="row mb-3">
              <label for="supportContact" class="col-sm-3 col-form-label">Support Contact</label>
              <div class="col-sm-9">
                <input
                  id="supportContact"
                  type="email"
                  class="form-control"
                  v-model="partnerSettings.supportContact"
                />
              </div>
            </div>

            <div class="row mb-3">
              <label for="maxClients" class="col-sm-3 col-form-label">Maximum Clients</label>
              <div class="col-sm-9">
                <input
                  id="maxClients"
                  type="number"
                  class="form-control"
                  v-model="partnerSettings.maxClients"
                  min="1"
                />
                <div class="form-text">Maximum number of clients you can manage</div>
              </div>
            </div>
          </div>
        </div>

        <!-- API Configuration -->
        <div class="card mb-4">
          <div class="card-body">
            <h2 class="card-title">API Configuration</h2>

            <div class="row mb-3">
              <label for="apiKey" class="col-sm-3 col-form-label">API Key</label>
              <div class="col-sm-9">
                <div class="input-group">
                  <input
                    id="apiKey"
                    :type="apiSettings.apiKeyVisible ? 'text' : 'password'"
                    class="form-control font-monospace"
                    :value="apiSettings.apiKey"
                    readonly
                  />
                  <button
                    class="bb-btn-secondary-cta"
                    type="button"
                    @click="toggleApiKeyVisibility"
                  >
                    <i :class="apiSettings.apiKeyVisible ? 'bi-eye-slash' : 'bi-eye'" class="me-1"></i>
                    {{ apiSettings.apiKeyVisible ? "Hide" : "Show" }}
                  </button>
                  <button
                    class="bb-btn-secondary-cta"
                    type="button"
                    @click="regenerateApiKey"
                  >
                    <i class="bi bi-arrow-clockwise me-1"></i>Regenerate
                  </button>
                </div>
              </div>
            </div>

            <div class="row mb-3">
              <label for="apiUrl" class="col-sm-3 col-form-label">API URL</label>
              <div class="col-sm-9">
                <input
                  id="apiUrl"
                  type="url"
                  class="form-control"
                  v-model="partnerSettings.apiUrl"
                />
              </div>
            </div>

            <div class="row mb-3">
              <div class="col-sm-9 offset-sm-3">
                <div class="form-check">
                  <input
                    id="enableRateLimit"
                    class="form-check-input"
                    type="checkbox"
                    v-model="apiSettings.enableRateLimit"
                  />
                  <label class="form-check-label" for="enableRateLimit">
                    Enable rate limiting
                  </label>
                </div>
              </div>
            </div>

            <div class="row mb-3">
              <label for="rateLimit" class="col-sm-3 col-form-label">Rate Limit (req/hour)</label>
              <div class="col-sm-9">
                <input
                  id="rateLimit"
                  type="number"
                  class="form-control"
                  v-model="apiSettings.rateLimit"
                  :disabled="!apiSettings.enableRateLimit"
                />
              </div>
            </div>

            <div class="row mb-3">
              <div class="col-sm-9 offset-sm-3">
                <div class="form-check">
                  <input
                    id="enableLogging"
                    class="form-check-input"
                    type="checkbox"
                    v-model="apiSettings.enableLogging"
                  />
                  <label class="form-check-label" for="enableLogging">
                    Enable API request logging
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Notifications -->
        <div class="card mb-4">
          <div class="card-body">
            <h2 class="card-title">Notifications</h2>

            <div class="row mb-3">
              <div class="col-sm-9 offset-sm-3">
                <h3 class="h6 mb-3">Email Notifications</h3>
                <div class="form-check mb-2">
                  <input
                    id="emailOnNewClient"
                    class="form-check-input"
                    type="checkbox"
                    v-model="notificationSettings.emailOnNewClient"
                  />
                  <label class="form-check-label" for="emailOnNewClient">
                    New client registrations
                  </label>
                </div>
                <div class="form-check mb-2">
                  <input
                    id="emailOnClientError"
                    class="form-check-input"
                    type="checkbox"
                    v-model="notificationSettings.emailOnClientError"
                  />
                  <label class="form-check-label" for="emailOnClientError">
                    Client errors and issues
                  </label>
                </div>
                <div class="form-check mb-2">
                  <input
                    id="emailOnSystemUpdates"
                    class="form-check-input"
                    type="checkbox"
                    v-model="notificationSettings.emailOnSystemUpdates"
                  />
                  <label class="form-check-label" for="emailOnSystemUpdates">
                    System updates and maintenance
                  </label>
                </div>
              </div>
            </div>

            <div class="row mb-3">
              <div class="col-sm-9 offset-sm-3">
                <h3 class="h6 mb-3">Reports</h3>
                <div class="form-check mb-2">
                  <input
                    id="weeklyReports"
                    class="form-check-input"
                    type="checkbox"
                    v-model="notificationSettings.weeklyReports"
                  />
                  <label class="form-check-label" for="weeklyReports">
                    Weekly activity reports
                  </label>
                </div>
                <div class="form-check mb-2">
                  <input
                    id="monthlyReports"
                    class="form-check-input"
                    type="checkbox"
                    v-model="notificationSettings.monthlyReports"
                  />
                  <label class="form-check-label" for="monthlyReports">
                    Monthly summary reports
                  </label>
                </div>
              </div>
            </div>

            <div class="row mb-3">
              <div class="col-sm-9 offset-sm-3">
                <div class="form-check mb-3">
                  <input
                    id="enableWebhooks"
                    class="form-check-input"
                    type="checkbox"
                    v-model="partnerSettings.enableWebhooks"
                  />
                  <label class="form-check-label" for="enableWebhooks">
                    Enable webhook notifications
                  </label>
                </div>
              </div>
            </div>

            <div v-if="partnerSettings.enableWebhooks" class="row mb-3">
              <label for="webhookUrl" class="col-sm-3 col-form-label">Webhook URL</label>
              <div class="col-sm-9">
                <input
                  id="webhookUrl"
                  type="url"
                  class="form-control"
                  v-model="partnerSettings.webhookUrl"
                  placeholder="https://api.yourcompany.com/webhooks/partner"
                />
                <div class="form-text">
                  We'll send POST requests to this URL for important events
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Billing & Pricing -->
        <div class="card mb-4">
          <div class="card-body">
            <h2 class="card-title">Billing & Pricing</h2>

            <div class="row mb-3">
              <label for="billingModel" class="col-sm-3 col-form-label">Billing Model</label>
              <div class="col-sm-9">
                <select id="billingModel" class="form-select" v-model="billingSettings.billingModel">
                  <option value="per-client">Per Client</option>
                  <option value="flat-rate">Flat Rate</option>
                  <option value="usage-based">Usage Based</option>
                </select>
              </div>
            </div>

            <div class="row mb-3">
              <label for="monthlyFee" class="col-sm-3 col-form-label">Monthly Fee</label>
              <div class="col-sm-9">
                <div class="input-group" style="max-width: 200px">
                  <span class="input-group-text">$</span>
                  <input
                    id="monthlyFee"
                    type="number"
                    class="form-control"
                    v-model="billingSettings.monthlyFee"
                    step="0.01"
                  />
                </div>
              </div>
            </div>

            <div class="row mb-3">
              <label for="currency" class="col-sm-3 col-form-label">Currency</label>
              <div class="col-sm-9">
                <select id="currency" class="form-select" style="max-width: 200px" v-model="billingSettings.currency">
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                  <option value="GBP">GBP</option>
                </select>
              </div>
            </div>

            <div class="row mb-3">
              <label for="billingContact" class="col-sm-3 col-form-label">Billing Contact</label>
              <div class="col-sm-9">
                <input
                  id="billingContact"
                  type="email"
                  class="form-control"
                  v-model="partnerSettings.billingContact"
                />
              </div>
            </div>

            <div class="row mb-3">
              <div class="col-sm-9 offset-sm-3">
                <div class="form-check">
                  <input
                    id="autoInvoicing"
                    class="form-check-input"
                    type="checkbox"
                    v-model="billingSettings.autoInvoicing"
                  />
                  <label class="form-check-label" for="autoInvoicing">
                    Enable automatic invoicing
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Form Actions -->
        <div class="row mb-4">
          <div class="col-sm-9 offset-sm-3">
            <div class="d-flex justify-content-end gap-2">
              <button
                type="button"
                class="bb-btn-cancel"
                @click="router.push('/partners')"
              >
                Cancel
              </button>
              <button type="submit" class="bb-btn-primary-cta" :disabled="isSaving">
                <i class="bi bi-check-circle me-1"></i>
                <span v-if="isSaving">Saving...</span>
                <span v-else>Save Changes</span>
              </button>
            </div>
          </div>
        </div>

        <!-- Danger Zone -->
        <div class="card border-danger mb-4">
          <div class="card-body">
            <h2 class="card-title text-danger">Danger Zone</h2>
            <div class="d-flex justify-content-between align-items-center">
              <div>
                <strong>Delete Partner Account</strong>
                <div class="text-muted small">
                  This will permanently delete your partner account and all associated data
                </div>
              </div>
              <button
                type="button"
                class="bb-btn-danger"
                @click="showDeleteModal = true"
              >
                <i class="bi bi-trash me-2"></i>Delete Account
              </button>
            </div>
          </div>
        </div>
      </form>
    </PanelCard>
  </PanelGrid>

  <!-- Delete Confirmation Modal -->
  <div
    v-if="showDeleteModal"
    class="modal fade show d-block"
    tabindex="-1"
    role="dialog"
    @click.self="showDeleteModal = false"
  >
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h2 class="modal-title">Delete Partner Account</h2>
          <button
            type="button"
            class="btn-close"
            @click="showDeleteModal = false"
            aria-label="Close"
          ></button>
        </div>
        <div class="modal-body">
          <p>
            Are you sure you want to delete your partner account? This action
            <strong>cannot be undone</strong> and will permanently remove all
            associated clients, projects, and data.
          </p>
        </div>
        <div class="modal-footer">
          <button
            type="button"
            class="bb-btn-cancel"
            @click="showDeleteModal = false"
          >
            Cancel
          </button>
          <button type="button" class="btn btn-danger" @click="confirmDeleteAccount">
            <i class="bi bi-trash me-1"></i>Delete Account
          </button>
        </div>
      </div>
    </div>
  </div>
  <div v-if="showDeleteModal" class="modal-backdrop fade show" @click="showDeleteModal = false"></div>
</template>
